import React from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import RefreshIcon from '@mui/icons-material/Refresh';
import { canUpdateCohort } from '../../utils/permissionKeys';
import {
  siError,
  useAllCohorts,
  useRanking,
  useRecompute,
} from '../../hooks/useStudentRankings';
import Leaderboard from './Leaderboard';
import ModuleRankings from './ModuleRankings';
import WeightsPanel from './WeightsPanel';
import { CARD, INPUT, updatedText } from './rankingText';

const TABS = [
  { key: 'leaderboard', label: 'Leaderboard' },
  { key: 'modules', label: 'Modules' },
  { key: 'weights', label: 'Score weights' },
];

// Who's doing best in a cohort, in which modules, and who's struggling.
// Cohort and tab live in the URL so a view can be shared.
export default function RankingsView({
  params,
  setParams,
  privacyRestricted,
  onOpenLearner,
}) {
  const permissions = useSelector((state) => state.auth.permissions ?? []);
  const role = useSelector((state) => state.auth.userDetails?.role);
  const cohortId = params.get('cohort') || '';
  const tab = TABS.some((t) => t.key === params.get('rtab'))
    ? params.get('rtab')
    : 'leaderboard';

  const { data: cohortsData, isLoading: loadingCohorts } = useAllCohorts();
  const cohorts = cohortsData?.data?.cohorts ?? [];
  // Cheap first page, for the header's "updated" time and the module list.
  const overview = useRanking(cohortId, {}, 0);
  const recompute = useRecompute(cohortId);

  const update = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setParams(next, { replace: true });
  };

  return (
    <div className="mt-4 space-y-4 pb-20">
      <div className={`${CARD} flex flex-wrap items-center gap-3 p-4`}>
        <select
          aria-label="Cohort"
          value={cohortId}
          onChange={(e) => update({ cohort: e.target.value })}
          className={`${INPUT} min-w-[18rem] max-w-md`}
          disabled={loadingCohorts}
        >
          <option value="">
            {loadingCohorts ? 'Loading cohorts…' : 'Choose a cohort…'}
          </option>
          {cohorts.map((cohort) => (
            <option key={cohort.id} value={cohort.id}>
              {cohort.name}
              {cohort.status ? ` (${cohort.status})` : ''}
            </option>
          ))}
        </select>
        {cohortId && overview.data?.generated_at && (
          <span className="text-xs text-gray-500">
            {updatedText(overview.data.generated_at)}
          </span>
        )}
        {cohortId && (
          <button
            type="button"
            onClick={() =>
              recompute.mutate(undefined, {
                onSuccess: () => toast.success('Rankings recalculated.'),
                onError: (err) =>
                  toast.error(
                    siError(err, "We couldn't recalculate the rankings.")
                  ),
              })
            }
            disabled={recompute.isLoading || overview.isError}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshIcon
              fontSize="small"
              className={recompute.isLoading ? 'animate-spin' : ''}
            />
            {recompute.isLoading ? 'Recalculating…' : 'Refresh'}
          </button>
        )}
      </div>

      {!cohortId && (
        <div className={`${CARD} p-12 text-center`}>
          <p className="text-sm text-gray-700">
            Choose a cohort to rank its learners.
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Learners are scored on quizzes, assignments, content completed and
            engagement, overall and per module.
          </p>
        </div>
      )}

      {cohortId && overview.isError && !overview.data && (
        <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
          {siError(overview.error, "We couldn't load the rankings.")}
        </div>
      )}

      {cohortId && !(overview.isError && !overview.data) && (
        <>
          <nav className="flex flex-wrap gap-1 border-b border-gray-200">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() =>
                  update({ rtab: t.key === 'leaderboard' ? '' : t.key })
                }
                className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
                  tab === t.key
                    ? 'border-persian-500 text-persian-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
          {tab === 'leaderboard' && (
            <Leaderboard
              cohortId={cohortId}
              modules={overview.data?.modules ?? []}
              privacyRestricted={privacyRestricted}
              onOpenLearner={(studentId) => onOpenLearner(studentId, cohortId)}
            />
          )}
          {tab === 'modules' && (
            <ModuleRankings
              cohortId={cohortId}
              onOpenLearner={(studentId) => onOpenLearner(studentId, cohortId)}
            />
          )}
          {tab === 'weights' && (
            <WeightsPanel
              cohortId={cohortId}
              canEdit={canUpdateCohort(permissions, role)}
            />
          )}
        </>
      )}
    </div>
  );
}
